import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ShieldQuestion } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams, FetchResult } from '@/components/DataTable/types'
import { fetchSupportTickets, type AdminSupportTicket } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { Tag } from '@/components/StatusBadge'
import { TicketThread, isAppealTicket } from './TicketThread'

type AppealKind = 'freeze' | 'ban' | 'channel'

function appealKindOf(ticket: AdminSupportTicket): AppealKind | null {
  for (const m of ticket.messages) {
    if (m.text.startsWith('[Заморозка]')) return 'freeze'
    if (m.text.startsWith('[Бан]')) return 'ban'
    if (m.text.startsWith('[ChannelAppeal:')) return 'channel'
  }
  return null
}

/**
 * A7b — апеляції на бан/заморозку акаунта та бан каналу/чату (тег "[Заморозка]"/"[Бан]" від Grom
 * SpamBot, "[ChannelAppeal:N]" від Grom Abuse Notifications). Окремий розділ від /support, щоб
 * "людина оскаржує обмеження" не губилося серед звичайних звернень до підтримки - той самий тікет
 * SupportTicketAggregate, лише інша вибірка на клієнті (бекенд не розрізняє типи тікетів).
 */
export function AppealsScreen() {
  const t = useT()
  const [reloadKey, setReloadKey] = useState(0)

  const fetcher = useCallback(async (params: FetchParams): Promise<FetchResult<AdminSupportTicket>> => {
    const result = await fetchSupportTickets(params)
    return { ...result, rows: result.rows.filter((r) => isAppealTicket(r)) }
  }, [])

  const kindLabel: Record<AppealKind, string> = {
    freeze: t.support.appealKindFreeze,
    ban: t.support.appealKindBan,
    channel: t.support.appealKindChannel,
  }

  const columns = useMemo<AdminColumn<AdminSupportTicket>[]>(
    () => [
      {
        key: 'user_id',
        header: t.support.user,
        type: 'text',
        width: 160,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.user_id}`}
          </Link>
        ),
      },
      {
        key: 'kind',
        header: t.support.appealKind,
        type: 'text',
        width: 130,
        sortable: false,
        render: (r) => {
          const kind = appealKindOf(r)
          return kind ? <Tag>{kindLabel[kind]}</Tag> : '—'
        },
      },
      {
        key: 'status',
        header: t.support.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'open', label: t.support.statusOpen, status: 'review' },
          { value: 'closed', label: t.support.statusClosed, status: 'deleted' },
        ],
      },
      { key: 'created_at', header: t.support.createdAt, type: 'date', width: 140 },
    ],
    [t, kindLabel],
  )

  return (
    <>
      <PageHeader title={t.nav.appeals} screenId="A7b.01" verified />
      <DataTable<AdminSupportTicket>
        key={reloadKey}
        screenId="A7b.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.id}
        emptyState={{ icon: <ShieldQuestion size={28} strokeWidth={1.5} />, title: t.support.appealsEmptyTitle }}
        detailPanel={(r) => (
          <TicketThread ticket={r} onChanged={() => setReloadKey((k) => k + 1)} />
        )}
      />
    </>
  )
}
